import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Monitor, Tablet, Smartphone, RefreshCw, Crosshair, Eye, Upload, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { generateResponsiveVersions } from '@/lib/responsiveImages';
import FocalPointEditor from './FocalPointEditor';
import DevicePreview from './DevicePreview';

const VERSIONS = [
  { key: 'desktop', label: 'Desktop', target: '2400px', Icon: Monitor },
  { key: 'tablet', label: 'Tablet', target: '1600px', Icon: Tablet },
  { key: 'mobile', label: 'Mobile', target: '1200px', Icon: Smartphone },
];

export default function ResponsiveVersionsPanel({ imageUrl, responsive, onChange, fieldId }) {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [showFocal, setShowFocal] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [uploadingOverride, setUploadingOverride] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const versions = await generateResponsiveVersions(imageUrl, (name, i, total) => setProgress(`${name} (${i}/${total})`));
      onChange({ ...(responsive || {}), ...versions, applied: false });
      toast.success('Versioni responsive generate — ricorda di salvare il progetto');
    } catch (err) {
      toast.error(err.message || 'Generazione non riuscita');
    }
    setGenerating(false);
    setProgress('');
  };

  const handleOverrideUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingOverride(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      onChange({ ...(responsive || {}), mobile_override: file_url });
      toast.success('Immagine alternativa mobile caricata');
    } catch {
      toast.error('Errore nel caricamento');
    }
    setUploadingOverride(false);
    e.target.value = '';
  };

  const mobileSrc = responsive?.mobile_override || responsive?.mobile?.url || imageUrl;
  const applied = responsive?.applied === true;

  return (
    <div className="rounded-lg border border-dashed border-[#d5d5d5] bg-[#fafafa] p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="font-medium">Versioni responsive</Label>
        <Button type="button" variant="outline" size="sm" disabled={generating} onClick={handleGenerate}>
          <RefreshCw className={`mr-1 h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
          {generating ? `Generazione ${progress}...` : responsive?.desktop ? 'Rigenera versioni' : 'Genera versioni'}
        </Button>
      </div>

      {!responsive?.desktop && !generating && (
        <p className="text-xs text-[#7a7a7a]">
          Genera automaticamente versioni WebP compresse per Desktop (2400px), Tablet (1600px) e Mobile (1200px), senza ritagli né deformazioni.
        </p>
      )}

      {responsive?.desktop && (
        <>
          <div className="grid grid-cols-3 gap-3">
            {VERSIONS.map(({ key, label, target, Icon }) => {
              const v = responsive[key];
              const src = key === 'mobile' ? mobileSrc : v?.url;
              return (
                <div key={key} className="space-y-1">
                  <div className="flex items-center gap-1 text-xs text-[#7a7a7a]"><Icon className="h-3.5 w-3.5" /> {label} · {target}</div>
                  {src && <img src={src} alt={`Versione ${label}`} loading="lazy" className="h-20 w-full rounded object-cover" />}
                  {v && <p className="text-[10px] text-[#9a9a9a]">{v.width}×{v.height}px</p>}
                </div>
              );
            })}
          </div>

          {/* Mobile source mode */}
          <div className="space-y-2">
            <Label className="text-xs text-[#7a7a7a]">Versione mobile</Label>
            <RadioGroup
              value={responsive.mobile_override ? 'override' : 'auto'}
              onValueChange={(mode) => {
                if (mode === 'auto') onChange({ ...responsive, mobile_override: '' });
                else document.getElementById(`mobile-override-${fieldId}`).click();
              }}
              className="flex flex-wrap gap-4"
            >
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="auto" /> Generazione automatica</label>
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="override" /> Usa immagine alternativa mobile</label>
            </RadioGroup>
            {responsive.mobile_override && (
              <div className="flex items-center gap-2">
                <img src={responsive.mobile_override} alt="Alternativa mobile" className="h-14 w-14 rounded object-cover" />
                <Button type="button" variant="outline" size="sm" disabled={uploadingOverride} onClick={() => document.getElementById(`mobile-override-${fieldId}`).click()}>
                  <Upload className="mr-1 h-4 w-4" /> {uploadingOverride ? 'Caricamento...' : 'Sostituisci'}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...responsive, mobile_override: '' })}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            )}
            <input id={`mobile-override-${fieldId}`} type="file" accept="image/*" className="hidden" onChange={handleOverrideUpload} />
          </div>

          {/* Apply / discard */}
          <div className="flex flex-wrap items-center gap-2 rounded border border-[#e5e5e5] bg-white p-3">
            <span className={`text-xs ${applied ? 'text-green-700' : 'text-[#9a7a3a]'}`}>
              {applied ? 'Versioni attive sul sito' : 'Anteprima — non ancora applicate al sito'}
            </span>
            <div className="ml-auto flex gap-2">
              {!applied && (
                <Button type="button" size="sm" onClick={() => { onChange({ ...responsive, applied: true }); toast.success('Versioni applicate al sito — salva il progetto'); }}>
                  <Check className="mr-1 h-4 w-4" /> Adatta al sito
                </Button>
              )}
              {applied && (
                <Button type="button" variant="outline" size="sm" onClick={() => { onChange({ ...responsive, applied: false }); toast.success('Versioni disattivate — si torna all\'immagine originale'); }}>
                  <X className="mr-1 h-4 w-4" /> Disattiva sul sito
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => { onChange(undefined); setShowFocal(false); setShowDevices(false); toast.success('Versioni eliminate'); }}>
                <Trash2 className="mr-1 h-4 w-4" /> Elimina versioni
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant={showFocal ? 'secondary' : 'outline'} size="sm" onClick={() => setShowFocal(!showFocal)}>
              <Crosshair className="mr-1 h-4 w-4" /> Punto focale
            </Button>
            <Button type="button" variant={showDevices ? 'secondary' : 'outline'} size="sm" onClick={() => setShowDevices(!showDevices)}>
              <Eye className="mr-1 h-4 w-4" /> Anteprima dispositivi
            </Button>
          </div>

          {showFocal && (
            <FocalPointEditor
              imageUrl={mobileSrc}
              focusX={responsive.focus_x ?? 50}
              focusY={responsive.focus_y ?? 50}
              onChange={(focus_x, focus_y) => onChange({ ...responsive, focus_x, focus_y })}
            />
          )}

          {showDevices && <DevicePreview responsive={responsive} originalUrl={imageUrl} />}
        </>
      )}
    </div>
  );
}